<script setup>
import { computed, onMounted, ref } from 'vue'
import { api } from '../api'
import { useI18n } from '../i18n'
import { formatBytes, formatDate, formatDuration, runKind, runOutcome } from '../monitoring'

const { locale, t } = useI18n()
const metrics = ref(null)
const loading = ref(true)
const refreshing = ref(false)
const error = ref('')
const page = ref(1)
const pageSize = 10

const reportMetrics = computed(() => metrics.value?.reports ?? null)
const runs = computed(() => reportMetrics.value?.recent ?? [])
const totalPages = computed(() => Math.max(1, Math.ceil(runs.value.length / pageSize)))
const pagedRuns = computed(() => runs.value.slice((page.value - 1) * pageSize, page.value * pageSize))
const firstItem = computed(() => runs.value.length ? ((page.value - 1) * pageSize) + 1 : 0)
const lastItem = computed(() => Math.min(runs.value.length, page.value * pageSize))

async function load() {
  refreshing.value = true
  error.value = ''
  try {
    metrics.value = await api('/admin/metrics')
    if (page.value > totalPages.value) page.value = totalPages.value
  } catch (cause) {
    error.value = cause.message || t('metricsUnavailable')
  } finally {
    refreshing.value = false
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <section class="history-page">
    <RouterLink class="back-link" :to="{ name: 'admin-monitoring' }">{{ t('backToMonitoring') }}</RouterLink>
    <div class="page-title history-page-title"><div><p class="eyebrow">{{ t('liveOperations') }}</p><h1>{{ t('runHistoryTitle') }}</h1><p class="muted">{{ t('runHistoryHint') }}</p></div><button class="button button-small" type="button" :disabled="refreshing" @click="load">{{ refreshing ? t('refreshing') : t('refresh') }}</button></div>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <p v-else-if="loading" class="muted" aria-live="polite">{{ t('loading') }}</p>
    <section v-else class="card history-table-panel">
      <div v-if="reportMetrics" class="section-heading"><p class="muted">{{ t('showing', { from: firstItem, to: lastItem, total: runs.length }) }}</p><span class="muted">{{ t('completedRuns', { count: reportMetrics.completedCount }) }} · {{ t('failedRuns', { count: reportMetrics.failedCount }) }}</span></div>
      <p v-if="!runs.length" class="muted">{{ t('noRecentRuns') }}</p>
      <div v-else class="table-wrap"><table><thead><tr><th>{{ t('template') }}</th><th>{{ t('type') }}</th><th>{{ t('started') }}</th><th>{{ t('duration') }}</th><th>{{ t('resultSize') }}</th><th>{{ t('heapChange') }}</th><th>{{ t('outcome') }}</th></tr></thead><tbody>
        <tr v-for="run in pagedRuns" :key="run.id"><td><strong>{{ run.templateName }}</strong></td><td>{{ runKind(t, run.kind) }}</td><td>{{ formatDate(locale, run.startedAt) }}</td><td>{{ formatDuration(run.durationMs) }}</td><td>{{ formatBytes(run.resultBytes) }}</td><td>{{ formatBytes(run.heapDeltaBytes, true) }}</td><td><span class="run-state" :class="run.status">{{ runOutcome(t, run) }}<small v-if="run.errorCode">{{ run.errorCode }}</small></span></td></tr>
      </tbody></table></div>
      <nav v-if="runs.length > pageSize" class="history-pagination" :aria-label="t('pageNavigation')"><button class="catalog-page" type="button" :disabled="page <= 1" @click="page -= 1">‹ {{ t('previous') }}</button><span>{{ t('pageOf', { current: page, total: totalPages }) }}</span><button class="catalog-page" type="button" :disabled="page >= totalPages" @click="page += 1">{{ t('next') }} ›</button></nav>
    </section>
  </section>
</template>
